/**
 * Circuit breaker implementation for fault tolerance in network requests.
 * Prevents cascading failures by temporarily blocking requests when error threshold is exceeded.
 * Follows the three-state pattern: CLOSED → OPEN → HALF_OPEN → CLOSED.
 *
 * States:
 * - CLOSED: Normal operation, requests allowed
 * - OPEN: Failures exceeded threshold, requests blocked
 * - HALF_OPEN: Testing if service recovered, limited requests allowed
 */
export default class CircuitBreaker {
  /**
   * Creates a new CircuitBreaker instance with configurable failure handling.
   *
   * @param {Object} [config={}] - Circuit breaker configuration
   * @param {number} [config.failureThreshold=5] - Number of failures before opening circuit
   * @param {number} [config.timeout=30000] - Time in ms before attempting to close circuit
   */
  constructor(config) {
    this.config = {
      failureThreshold: 5,
      timeout: 30_000,
      ...config,
    };

    this.state = 'CLOSED';
    this.failures = 0;
    this.lastFailureTime = null;
    this.nextAttemptTime = null;
    this.trips = 0;
  }

  /**
   * Determines if a request should be allowed based on circuit state.
   * Automatically transitions from OPEN to HALF_OPEN when timeout expires.
   * @returns {boolean} True if request is allowed, false if circuit is open
   */
  isRequestAllowed() {
    const now = Date.now();

    if (this.state === 'OPEN') {
      if (now >= this.nextAttemptTime) {
        this.state = 'HALF_OPEN';
        return true;
      }

      return false;
    }

    return true;
  }

  /**
   * Records a successful operation and potentially closes the circuit.
   * Transitions from HALF_OPEN to CLOSED state and resets failure counter.
   */
  recordSuccess() {
    if (this.state === 'HALF_OPEN') {
      this.state = 'CLOSED';
      this.failures = 0;
    }
  }

  /**
   * Records a failed operation and potentially opens the circuit.
   * Opens circuit when failure count reaches threshold, setting timeout period.
   */
  recordFailure() {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.failures >= this.config.failureThreshold) {
      this.state = 'OPEN';
      this.nextAttemptTime = Date.now() + this.config.timeout;
      this.trips++;
    }
  }

  /**
   * Returns current circuit breaker state and statistics for monitoring.
   * @returns {Object} Circuit breaker state information
   */
  getState() {
    return {
      state: this.state,
      failures: this.failures,
      trips: this.trips,
      lastFailureTime: this.lastFailureTime,
      nextAttemptTime: this.nextAttemptTime,
    };
  }

  /**
   * Resets circuit breaker to initial CLOSED state.
   * Clears all failure counts and timestamps. Use for manual recovery.
   */
  reset() {
    this.state = 'CLOSED';
    this.failures = 0;
    this.lastFailureTime = null;
    this.nextAttemptTime = null;
  }
}
